import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import {
  X,
  ArrowRight,
  Plant,
  CloudSun,
  Leaf,
  ChatCircleDots,
  CheckCircle,
  Basket,
  Megaphone,
  Microphone,
} from "@phosphor-icons/react";
import { fpPath } from "@/lib/site";
import { http } from "@/lib/api";

/**
 * WelcomeOverlay — the warm "you're in, here's what to do next" screen shown
 * right after a successful sign-in.
 *
 *   mode="fullscreen"  → first-time farmers get a full-bleed welcome
 *   mode="modal"       → returning farmers get a compact dismissible card
 *
 * The four next-step tools are personalized from the farmer's own setup
 * (their farms + crops via `/api/farmers/farms/mine`) so a fresh account is
 * nudged to set up a farm, while a grower with crops sees harvest + selling
 * tools instead.
 */

const TOOL = {
  setupFarm: {
    key: "set-up-farm",
    title: "Set Up My Farm",
    desc: "Add your farm in 4 quick steps — powers every tool below.",
    to: fpPath("/farms/new"),
    Icon: Plant,
  },
  weather: {
    key: "my-weather",
    title: "My Weather",
    desc: "Local forecast turned into plain-language farm actions.",
    to: fpPath("/weather"),
    Icon: CloudSun,
  },
  plant: {
    key: "what-to-plant",
    title: "What Should I Plant?",
    desc: "Best crops for your location and month, right now.",
    to: fpPath("/plan-what-to-plant"),
    Icon: Leaf,
  },
  harvest: {
    key: "what-to-harvest",
    title: "What Should I Harvest?",
    desc: "See which of your crops are ready to pick and sell.",
    to: fpPath("/what-to-harvest"),
    Icon: Basket,
  },
  marketing: {
    key: "ai-marketing",
    title: "AI Marketing Assistant",
    desc: "Turn a harvest into ready-to-post promotions.",
    to: fpPath("/marketing"),
    Icon: Megaphone,
  },
  diary: {
    key: "voice-diary",
    title: "Voice Farm Diary",
    desc: "Log today's work by voice, in your language.",
    to: fpPath("/voice-diary"),
    Icon: Microphone,
  },
  ask: {
    key: "ask-ai",
    title: "Ask FarmersPulse AI",
    desc: "Get answers on crops, pests, markets and more.",
    to: fpPath("/ask"),
    Icon: ChatCircleDots,
  },
};

const GENERIC_STEPS = [TOOL.setupFarm, TOOL.weather, TOOL.plant, TOOL.ask];

function firstName(user) {
  const n = user?.name || user?.display_name || user?.displayName || "";
  if (n) return n.trim().split(/\s+/)[0];
  const email = user?.email || "";
  if (email) return email.split("@")[0];
  return "farmer";
}

// Pick the four best next-step tools from the farmer's setup answers.
function buildSteps(farms) {
  const list = Array.isArray(farms) ? farms : [];
  if (!list.length) return { steps: GENERIC_STEPS, hasFarm: false };

  const farm = list[0];
  const farmName = (farm.name || "").trim();
  const crops = list.flatMap((f) => f.crops || []).filter(Boolean);
  const firstCrop = crops[0];

  const weather = {
    ...TOOL.weather,
    desc: farmName
      ? `Forecast for ${farmName}, turned into farm actions.`
      : TOOL.weather.desc,
  };

  const steps = [weather];
  if (crops.length) {
    steps.push({
      ...TOOL.harvest,
      desc: firstCrop
        ? `See when your ${firstCrop.toLowerCase()} and other crops are ready.`
        : TOOL.harvest.desc,
    });
    steps.push({
      ...TOOL.marketing,
      desc: firstCrop
        ? `Promote your ${firstCrop.toLowerCase()} — captions, flyers & more.`
        : TOOL.marketing.desc,
    });
  } else {
    steps.push(TOOL.plant);
    steps.push(TOOL.diary);
  }
  steps.push(TOOL.ask);
  return { steps: steps.slice(0, 4), hasFarm: true };
}

export default function WelcomeOverlay({ open, mode = "modal", user, onClose }) {
  const fullscreen = mode === "fullscreen";
  const name = firstName(user);
  const [steps, setSteps] = useState(GENERIC_STEPS);
  const [hasFarm, setHasFarm] = useState(null); // null = unknown yet

  useEffect(() => {
    if (!open) return;
    let alive = true;
    setSteps(GENERIC_STEPS);
    setHasFarm(null);
    (async () => {
      try {
        const { data } = await http.get("/farmers/farms/mine");
        if (!alive) return;
        const built = buildSteps(data?.farms);
        setSteps(built.steps);
        setHasFarm(built.hasFarm);
      } catch {
        if (alive) { setSteps(GENERIC_STEPS); setHasFarm(false); }
      }
    })();
    return () => { alive = false; };
  }, [open]);

  const lead = fullscreen
    ? hasFarm
      ? "Your farm's all set. Here are the best four things to do next — start anywhere, everything talks to your farm."
      : "Your FarmersPulse account is ready. Here are the best four things to do next — start anywhere, everything talks to your farm."
    : hasFarm
      ? "Good to see you back. Jump straight into what your farm needs today."
      : "Good to see you back. Jump straight into what you need.";

  const cards = (
    <div
      className={`grid gap-3 ${fullscreen ? "sm:grid-cols-2" : "grid-cols-1"}`}
      data-testid="welcome-steps"
    >
      {steps.map(({ key, title, desc, to, Icon }) => (
        <Link
          key={key}
          to={to}
          onClick={onClose}
          data-testid={`welcome-step-${key}`}
          className="group flex items-start gap-3 rounded-xl border border-black/12 bg-white/70 hover:bg-white px-4 py-3.5 text-left transition-colors hover:border-[color:var(--np-brand,#B85F2A)]"
        >
          <span className="shrink-0 grid place-items-center h-10 w-10 rounded-lg bg-[color:var(--np-brand,#B85F2A)]/10 text-[color:var(--np-brand,#B85F2A)]">
            <Icon size={22} weight="duotone" />
          </span>
          <span className="min-w-0">
            <span className="flex items-center gap-1.5 font-serif-display text-lg leading-tight text-[color:var(--np-text,#151312)]">
              {title}
              <ArrowRight
                size={15}
                weight="bold"
                className="opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all text-[color:var(--np-brand,#B85F2A)]"
              />
            </span>
            <span className="block text-sm text-[color:var(--np-text-2,#3A3830)] leading-snug mt-0.5">
              {desc}
            </span>
          </span>
        </Link>
      ))}
    </div>
  );

  const overlay = (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="welcome-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[1100] flex items-center justify-center px-3 md:px-4"
          style={{ height: "100dvh" }}
        >
          <div className="absolute inset-0 bg-black/70 backdrop-blur-md" onClick={onClose} />
          <motion.div
            initial={{ y: 28, scale: 0.98, opacity: 0 }}
            animate={{ y: 0, scale: 1, opacity: 1 }}
            exit={{ y: 20, scale: 0.98, opacity: 0 }}
            transition={{ type: "spring", damping: 26, stiffness: 240 }}
            className={`relative w-full ${fullscreen ? "max-w-2xl" : "max-w-md"} flex flex-col bg-[color:var(--np-bg,#F5EBDC)] border border-black/15 shadow-2xl rounded-2xl overflow-hidden`}
            style={{ maxHeight: "90dvh" }}
          >
            <button
              onClick={onClose}
              data-testid="welcome-close"
              className="absolute top-4 right-4 p-1 text-[color:var(--np-text-2,#3A3830)] hover:text-[color:var(--np-text,#151312)] z-10"
              aria-label="Close"
            >
              <X size={18} weight="thin" />
            </button>

            <div className="overflow-y-auto px-6 md:px-8 py-7 md:py-8">
              <div className="flex items-center gap-2 font-mono-tech text-[10px] tracking-[0.35em] uppercase text-[color:var(--np-brand,#B85F2A)] mb-3">
                <CheckCircle size={16} weight="fill" className="text-emerald-500" />
                You're in
              </div>
              <h2
                className={`font-serif-display leading-[1.02] text-[color:var(--np-text,#151312)] ${fullscreen ? "text-4xl sm:text-5xl" : "text-3xl"}`}
              >
                Welcome, {name}.
              </h2>
              <p className="mt-3 text-base text-[color:var(--np-text-2,#3A3830)] leading-relaxed">
                {lead}
              </p>

              <div className="mt-6">{cards}</div>

              <button
                onClick={onClose}
                data-testid="welcome-dismiss"
                className="mt-6 w-full rounded-full border border-black/20 px-5 py-3 font-mono-tech text-[11px] tracking-[0.3em] uppercase text-[color:var(--np-text,#151312)] hover:bg-[color:var(--np-text,#151312)] hover:text-[color:var(--np-bg,#F5EBDC)] transition-colors"
              >
                Explore on my own
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  if (typeof document === "undefined") return null;
  return createPortal(overlay, document.body);
}
